import { createStaticRanges } from 'react-date-range';
import {
    add,
    addDays,
    addMonths,
    endOfDay,
    endOfMonth,
    endOfWeek,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
} from 'date-fns';
import { useMemo } from 'react';
import { DATE_PRESET_LABELS } from '@/state/legs/legsTypes';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { selectMemberTimezone } from '@/state/member/memberSelectors';
import { convertDateToSelectedTimezone } from '@/lib/helpers/helpers';

const useDatePreset = () => {
    const userTimezone = useSelector((state: RootState) => selectMemberTimezone(state)) || '';
    const defineds = useMemo(
        () => ({
            startOfWeek: startOfWeek(convertDateToSelectedTimezone(new Date(), userTimezone), {
                weekStartsOn: 1,
            }),
            endOfWeek: endOfDay(convertDateToSelectedTimezone(new Date(), userTimezone)),
            startOfLastWeek: startOfWeek(
                convertDateToSelectedTimezone(addDays(new Date(), -7), userTimezone),
                { weekStartsOn: 1 },
            ),
            endOfLastWeek: endOfWeek(
                convertDateToSelectedTimezone(addDays(new Date(), -7), userTimezone),
                { weekStartsOn: 1 },
            ),
            startOfToday: startOfDay(convertDateToSelectedTimezone(new Date(), userTimezone)),
            endOfToday: endOfDay(convertDateToSelectedTimezone(new Date(), userTimezone)),
            startOfYesterday: startOfDay(
                convertDateToSelectedTimezone(addDays(new Date(), -1), userTimezone),
            ),
            endOfYesterday: endOfDay(
                convertDateToSelectedTimezone(addDays(new Date(), -1), userTimezone),
            ),
            startOfMonth: startOfMonth(convertDateToSelectedTimezone(new Date(), userTimezone)),
            endOfMonth: endOfDay(convertDateToSelectedTimezone(new Date(), userTimezone)),
            startOfLastMonth: startOfMonth(
                convertDateToSelectedTimezone(addMonths(new Date(), -1), userTimezone),
            ),
            endOfLastMonth: endOfMonth(
                convertDateToSelectedTimezone(addMonths(new Date(), -1), userTimezone),
            ),
        }),
        [userTimezone],
    );

    const presetList = useMemo(
        () => [
            {
                label: DATE_PRESET_LABELS.ALL_TIME,
                range: () => ({
                    startDate: startOfDay(new Date('01/01/2016')),
                    endDate: endOfDay(new Date()),
                    label: DATE_PRESET_LABELS.ALL_TIME,
                }),
            },
            {
                label: DATE_PRESET_LABELS.TODAY,
                range: () => ({
                    startDate: defineds.startOfToday,
                    endDate: defineds.endOfToday,
                    label: DATE_PRESET_LABELS.TODAY,
                }),
            },
            {
                label: DATE_PRESET_LABELS.YESTERDAY,
                range: () => ({
                    startDate: defineds.startOfYesterday,
                    endDate: defineds.endOfYesterday,
                    label: DATE_PRESET_LABELS.YESTERDAY,
                }),
            },

            {
                label: DATE_PRESET_LABELS.THIS_WEEK,
                range: () => ({
                    startDate: defineds.startOfWeek,
                    endDate: defineds.endOfWeek,
                    label: DATE_PRESET_LABELS.THIS_WEEK,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_WEEK,
                range: () => ({
                    startDate: defineds.startOfLastWeek,
                    endDate: defineds.endOfLastWeek,
                    label: DATE_PRESET_LABELS.LAST_WEEK,
                }),
            },
            {
                label: DATE_PRESET_LABELS.THIS_MONTH,
                range: () => ({
                    startDate: defineds.startOfMonth,
                    endDate: defineds.endOfMonth,
                    label: DATE_PRESET_LABELS.THIS_MONTH,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_MONTH,
                range: () => ({
                    startDate: defineds.startOfLastMonth,
                    endDate: defineds.endOfLastMonth,
                    label: DATE_PRESET_LABELS.LAST_MONTH,
                }),
            },
            {
                label: DATE_PRESET_LABELS.SINCE_THIS_DATE,
                range: () => ({
                    startDate: convertDateToSelectedTimezone(addDays(new Date(), -3), userTimezone),
                    endDate: defineds.endOfToday,
                    label: DATE_PRESET_LABELS.SINCE_THIS_DATE,
                }),
                isSelected(range) {
                    return range.label === DATE_PRESET_LABELS.SINCE_THIS_DATE;
                },
            },
            {
                label: DATE_PRESET_LABELS.LAST_7_DAYS,
                range: () => ({
                    startDate: startOfDay(add(new Date(), { days: -7 })),
                    endDate: endOfDay(new Date()),
                    label: DATE_PRESET_LABELS.LAST_7_DAYS,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_30_DAYS,
                range: () => ({
                    startDate: startOfDay(add(new Date(), { days: -30 })),
                    endDate: endOfDay(new Date()),
                    label: DATE_PRESET_LABELS.LAST_30_DAYS,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_90_DAYS,
                range: () => ({
                    startDate: startOfDay(add(new Date(), { days: -90 })),
                    endDate: endOfDay(new Date()),
                    label: DATE_PRESET_LABELS.LAST_90_DAYS,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_YEAR,
                range: () => ({
                    startDate: startOfDay(add(new Date(), { months: -12 })),
                    endDate: endOfDay(new Date()),
                    label: DATE_PRESET_LABELS.LAST_YEAR,
                }),
            },
            {
                label: DATE_PRESET_LABELS.YEAR_TO_DATE,
                range: () => ({
                    startDate: startOfYear(new Date()),
                    endDate: endOfDay(new Date()),
                    label: DATE_PRESET_LABELS.YEAR_TO_DATE,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_10_TRADES,
                range: () => ({
                    startDate: startOfDay(new Date('01/01/2016')),
                    endDate: endOfDay(new Date('01/01/2016')),
                    label: DATE_PRESET_LABELS.LAST_10_TRADES,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_25_TRADES,
                range: () => ({
                    startDate: startOfDay(new Date('01/02/2016')),
                    endDate: endOfDay(new Date('01/02/2016')),
                    label: DATE_PRESET_LABELS.LAST_25_TRADES,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_50_TRADES,
                range: () => ({
                    startDate: startOfDay(new Date('01/06/2016')),
                    endDate: endOfDay(new Date('01/06/2016')),
                    label: DATE_PRESET_LABELS.LAST_50_TRADES,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_100_TRADES,
                range: () => ({
                    startDate: startOfDay(new Date('01/03/2016')),
                    endDate: endOfDay(new Date('01/03/2016')),
                    label: DATE_PRESET_LABELS.LAST_100_TRADES,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_250_TRADES,
                range: () => ({
                    startDate: startOfDay(new Date('01/04/2016')),
                    endDate: endOfDay(new Date('01/04/2016')),
                    label: DATE_PRESET_LABELS.LAST_250_TRADES,
                }),
            },
            {
                label: DATE_PRESET_LABELS.LAST_500_TRADES,
                range: () => ({
                    startDate: startOfDay(new Date('01/05/2016')),
                    endDate: endOfDay(new Date('01/05/2016')),
                    label: DATE_PRESET_LABELS.LAST_500_TRADES,
                }),
            },
        ],
        [defineds],
    );

    const customRanges = useMemo(() => {
        return createStaticRanges(presetList);
    }, []);

    const onlyDatesRanges = useMemo(() => {
        return createStaticRanges(
            presetList.filter((preset) => {
                return (
                    preset.label !== DATE_PRESET_LABELS.LAST_10_TRADES &&
                    preset.label !== DATE_PRESET_LABELS.LAST_25_TRADES &&
                    preset.label !== DATE_PRESET_LABELS.LAST_50_TRADES &&
                    preset.label !== DATE_PRESET_LABELS.LAST_100_TRADES &&
                    preset.label !== DATE_PRESET_LABELS.LAST_250_TRADES &&
                    preset.label !== DATE_PRESET_LABELS.LAST_500_TRADES
                );
            }),
        );
    }, []);

    return {
        customRanges,
        onlyDatesRanges,
        DATE_PRESET_LABELS,
        presetList,
    };
};

export default useDatePreset;
